import { useCallback, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { SortConfig } from '@pages/Admin/Campaign/ui/types';
import { CallDto } from '@shared/services/apiService/apiService';
import { flattenCallPages } from '@shared/services/apiService/realtimeCalls.utils';
import {
  CampaignCallsListArg,
  pushCall,
  useCampaignCallsPaginatedInfiniteQuery,
} from '@shared/services/apiService/realtimeListsApi';
import { useAppDispatch } from '@shared/store';

import { CallsFilterFormValue } from '../model/type';

const PAGE_LIMIT = 20;

const buildFilterArgs = (params: CallsFilterFormValue) => ({
  statuses: params.statuses !== 'all' ? params.statuses : undefined,
  types: params.types !== 'all' ? params.types : undefined,
  start: params.start?.length ? params.start : undefined,
  end: params.end?.length ? params.end : undefined,
  numberSearch: params.numberSearch?.length ? params.numberSearch : undefined,
  callIdSearch: params.callIdSearch?.length ? params.callIdSearch : undefined,
  endedReason: params.endedReason !== 'all' ? params.endedReason : undefined,
});

const toApiSort = (sortConfig: SortConfig): Pick<CampaignCallsListArg, 'sortBy' | 'sortOrder'> => ({
  sortBy: (sortConfig.key ?? 'createdAt') as NonNullable<CampaignCallsListArg['sortBy']>,
  sortOrder: sortConfig.direction === 'asc' ? 'ASC' : 'DESC',
});

export const useGetCalls = (
  campaignId?: string,
  params: CallsFilterFormValue = {},
  sortConfig: SortConfig = { key: 'createdAt', direction: 'desc' },
) => {
  const dispatch = useAppDispatch();

  const filterArgs = useMemo(
    () => buildFilterArgs(params),
    [
      params.statuses,
      params.types,
      params.start,
      params.end,
      params.numberSearch,
      params.callIdSearch,
      params.endedReason,
    ],
  );
  const apiSort = useMemo(() => toApiSort(sortConfig), [sortConfig]);

  const queryArg = useMemo<CampaignCallsListArg | undefined>(
    () =>
      campaignId ? { id: campaignId, limit: PAGE_LIMIT, ...filterArgs, ...apiSort } : undefined,
    [apiSort, campaignId, filterArgs],
  );

  // A filter or campaign switch always reloads, as it did before the list lived in the cache
  const { data, hasNextPage, fetchNextPage, refetch, isFetching, isLoading, isError } =
    useCampaignCallsPaginatedInfiniteQuery(queryArg ?? skipToken, {
      refetchOnMountOrArgChange: true,
    });

  const callsData = useMemo(() => (data ? flattenCallPages(data.pages) : []), [data]);

  // Apply a pushed call (e.g. from the socket) to the same cache entry the REST pages live in
  const upsertCall = useCallback(
    (call: CallDto) => {
      if (!queryArg) return;
      dispatch(pushCall(queryArg, call));
    },
    [dispatch, queryArg],
  );

  const loadMore = useCallback(async () => {
    if (!hasNextPage || isFetching) return;
    await fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetching]);

  const recipientRefetch = useCallback(async () => {
    if (!queryArg) return;
    await refetch();
  }, [queryArg, refetch]);

  return {
    callsData,
    upsertCall,
    hasMore: !!hasNextPage,
    loadMore,
    totalItems: data?.pages[0]?.meta.totalItems,
    isErrorRecipient: isError,
    recipientRefetch,
    recipientIsLoading: isLoading,
    isFetching,
  };
};
